import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { SessionInfo } from './types';

/** Interruptor GLOBAL `LLM_MODE`. Si cada agente responde real o simulado depende de SUS API keys (BYOK): ver `credentials` del agente. */
export interface LlmStatus {
  mode: 'live' | 'fake';
}
export interface StatusInfo {
  version: string;
  llm: LlmStatus;
}

export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: async (): Promise<SessionInfo> => {
    const s = await api.get<SessionInfo>('/admin/auth/me');
    api.setCsrf(s.csrfToken);
    return s;
  },
  staleTime: 5 * 60_000,
  retry: false,
});

export const statusQuery = queryOptions({
  queryKey: ['status'],
  queryFn: () => api.get<StatusInfo>('/admin/status'),
  staleTime: 60_000,
  retry: false,
});

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const s = await api.post<SessionInfo>('/admin/auth/login', input);
      api.setCsrf(s.csrfToken);
      return s;
    },
    onSuccess: (s) => {
      qc.clear();
      qc.setQueryData(sessionQuery.queryKey, s);
    },
  });
}

/** Cambiar la contraseña (obligatorio con una temporal): la API revoca las demás sesiones y levanta la bandera `mustChangePassword`. */
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<{ ok: true }>('/admin/auth/change-password', input),
    onSuccess: async () => {
      // La caché sigue diciendo `mustChangePassword: true` y en esta pantalla la consulta de sesión no tiene observadores: `invalidateQueries` no la vuelve a pedir y la guarda
      // devolvía al formulario («no pasa nada»). Se pide la sesión fresca (la API ya levantó la bandera) antes de navegar.
      await qc.fetchQuery({ ...sessionQuery, staleTime: 0 });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/admin/auth/logout'),
    onSettled: () => {
      api.setCsrf(null);
      qc.clear();
    },
  });
}
